import React, { useEffect, useState, useCallback } from "react";
import { adminGetOrders, adminUpdateStatus, adminExportCsv } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import { Loader2, RefreshCw, Package, IndianRupee, CheckCircle2, Truck, Download } from "lucide-react";

const STATUSES = [
  "created", "cod_confirmed", "paid", "paid_placeholder",
  "shipped", "delivered", "cancelled",
];

function statusColor(s) {
  if (["paid", "paid_placeholder", "delivered"].includes(s)) return "text-emerald-400 bg-emerald-500/10 border-emerald-500/30";
  if (s === "shipped") return "text-sky-400 bg-sky-500/10 border-sky-500/30";
  if (s === "cancelled") return "text-red-400 bg-red-500/10 border-red-500/30";
  return "text-amber-300 bg-amber-500/10 border-amber-500/30";
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
}

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <div className="glass flex items-center gap-3 rounded-2xl p-4">
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tint}`}>
        <Icon size={18} />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-500">{label}</div>
        <div className="font-display text-2xl text-white">{value}</div>
      </div>
    </div>
  );
}

export default function OrdersTab({ password, onUnauth }) {
  const [data, setData] = useState({ orders: [], stats: null });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminGetOrders(password);
      setData(res);
    } catch (err) {
      if (err?.response?.status === 401) { onUnauth(); return; }
      toast.error("Failed to load orders");
    } finally { setLoading(false); }
  }, [password, onUnauth]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (orderId, status) => {
    try {
      await adminUpdateStatus(password, orderId, status);
      setData((d) => ({ ...d, orders: d.orders.map((o) => o.id === orderId ? { ...o, status } : o) }));
      toast.success("Status updated");
    } catch { toast.error("Update failed"); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      await adminExportCsv(password);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const revenue = data.orders
    .filter((o) => ["paid", "paid_placeholder", "delivered", "shipped", "cod_confirmed"].includes(o.status))
    .reduce((sum, o) => sum + (o.total_amount || 0), 0);

  return (
    <>
      <div className="flex items-center justify-end gap-2">
        <button
          data-testid={TID.adminExportCsv}
          onClick={exportCsv}
          disabled={exporting || data.orders.length === 0}
          className="flex items-center gap-1.5 rounded-full border border-[#FF7A00]/40 bg-[#FF7A00]/10 px-3 py-2 text-xs font-semibold uppercase tracking-widest text-[#FF7A00] hover:bg-[#FF7A00]/20 disabled:opacity-50"
        >
          {exporting ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />}
          Export CSV
        </button>
        <button
          onClick={load}
          className="rounded-full border border-white/10 bg-white/5 p-2 hover:bg-white/10"
          aria-label="Refresh"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      <div data-testid={TID.adminStatCards} className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard icon={Package} label="Total Orders" value={data.stats?.total ?? 0} tint="bg-white/10 text-white" />
        <StatCard icon={IndianRupee} label="Revenue" value={`₹${revenue.toLocaleString("en-IN")}`} tint="bg-fire text-black" />
        <StatCard icon={CheckCircle2} label="Paid" value={data.stats?.paid ?? 0} tint="bg-emerald-500/20 text-emerald-400" />
        <StatCard icon={Truck} label="COD" value={data.stats?.cod ?? 0} tint="bg-amber-500/20 text-amber-300" />
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-neutral-950">
        <div className="flex items-center justify-between px-4 py-3 text-xs uppercase tracking-widest text-neutral-400">
          <span>Recent Orders</span>
          <span>{data.orders.length} shown</span>
        </div>
        <div className="overflow-x-auto">
          <table data-testid={TID.adminOrdersTable} className="w-full min-w-[900px] text-left text-sm">
            <thead className="border-y border-white/5 bg-white/5 text-[11px] uppercase tracking-widest text-neutral-400">
              <tr>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Customer</th>
                <th className="px-4 py-2">Address</th>
                <th className="px-4 py-2">Method</th>
                <th className="px-4 py-2">Coupon</th>
                <th className="px-4 py-2">Source</th>
                <th className="px-4 py-2 text-right">Amount</th>
                <th className="px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={8} className="px-4 py-10 text-center text-neutral-500">Loading…</td></tr>)}
              {!loading && data.orders.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-neutral-500">No orders yet.</td></tr>
              )}
              {data.orders.map((o) => (
                <tr key={o.id} data-testid={TID.adminOrderRow} className="border-b border-white/5 align-top hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-xs text-neutral-400">
                    {fmtDate(o.created_at)}
                    <div className="mt-1 text-[10px] text-neutral-600">#{o.id.slice(0, 8)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{o.address?.full_name}</div>
                    <div className="text-xs text-neutral-400">{o.address?.phone}</div>
                    {o.address?.email && <div className="text-xs text-neutral-500">{o.address.email}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-300">
                    {o.address?.address_line}, {o.address?.city}, {o.address?.state} - {o.address?.pincode}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-md border px-2 py-0.5 text-[11px] uppercase tracking-wider ${
                      o.payment_method === "prepaid" ? "border-[#FF7A00]/40 bg-[#FF7A00]/10 text-[#FF7A00]" : "border-white/10 bg-white/5 text-neutral-300"
                    }`}>
                      {o.payment_method}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {o.coupon_code ? (
                      <>
                        <span className="font-mono font-semibold text-emerald-400">{o.coupon_code}</span>
                        {o.discount > 0 && (<div className="text-[10px] text-neutral-500">-₹{o.discount}</div>)}
                      </>
                    ) : <span className="text-neutral-600">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-300">
                    {o.utm?.source ? (
                      <>
                        <div className="font-medium capitalize">{o.utm.source}</div>
                        {o.utm.campaign && <div className="text-[10px] text-neutral-500">{o.utm.campaign}</div>}
                        {o.utm.variant && <div className="text-[10px] text-[#FFC107]">v={o.utm.variant}</div>}
                      </>
                    ) : <span className="text-neutral-600">direct</span>}
                  </td>
                  <td className="px-4 py-3 text-right font-display text-lg">₹{o.total_amount}</td>
                  <td className="px-4 py-3">
                    <select
                      data-testid={TID.adminStatusSelect}
                      value={o.status}
                      onChange={(e) => changeStatus(o.id, e.target.value)}
                      className={`rounded-md border px-2 py-1 text-xs outline-none ${statusColor(o.status)}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s} className="bg-neutral-900 text-white">{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
